import {
  ChangeDetectorRef,
  Component,
  effect,
  inject,
  signal,
  ChangeDetectionStrategy,
} from '@angular/core';
import { NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { Store } from '@ngxs/store';
import { ToastContainerComponent } from '@shared/components/toast-container/toast-container.component';
import { CookieConsentComponent } from '@shared/ui/cookie-consent/cookie-consent.component';
import { ContactUsButtonComponent } from '@ui/contact-us-button/contact-us-button.component';
import { EmailVerificationBannerComponent } from '@ui/email-verification-banner/email-verification-banner.component';
import { Footer } from '@ui/footer/footer';
import { MobileNavComponent } from '@ui/mobile-nav/mobile-nav.component';
import { NavbarComponent } from '@ui/navbar/navbar';
import { AuthSelectors } from '@core/states/auth/auth.selectors';
import { LoadConversation, OpenMessagesDrawer, SelectConversation } from '@states/conversations';
import { filter } from 'rxjs/operators';
import { AnalyticsService } from './core/services/analytics';
import { ConsentService } from './core/services/consent/consent.service';
import { CanonicalService } from './core/services/seo/canonical.service';
import { BrowserService } from './core/services/storage.service';

@Component({
  selector: 'app-root',
  imports: [
    RouterOutlet,
    NavbarComponent,
    Footer,
    MobileNavComponent,
    EmailVerificationBannerComponent,
    ToastContainerComponent,
    CookieConsentComponent,
    ContactUsButtonComponent,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './app.html',
})
export class App {
  private router = inject(Router);
  private store = inject(Store);
  private cdr = inject(ChangeDetectorRef);
  private browser = inject(BrowserService);
  private analytics = inject(AnalyticsService);
  private consent = inject(ConsentService);
  // Eagerly instantiate so it starts listening to router events from the first navigation.
  // providedIn:'root' services are lazy — without this inject() they won't activate until
  // something else requests them.
  private readonly _canonical = inject(CanonicalService);
  protected readonly title = signal('hiremymotorhome');

  // Track current route for mobile nav - initialize with current route
  currentRoute = signal(this.router.url.split('/')[1] || 'home');

  // Conversation deep link (?openConversation=<id>, used by transactional
  // emails). Held here until the user is authenticated, then the messages
  // drawer is opened at that conversation and the param is stripped.
  private pendingConversationId = signal<string | null>(null);
  private currentUser = this.store.selectSignal(AuthSelectors.getUser);

  // Captured landing page so we can replay the page_view if the user grants
  // analytics consent after the first NavigationEnd has already fired.
  private landingPath: string | null = null;
  private landingTitle: string | null = null;
  private landingHref: string | null = null;
  // Initialised from current consent state so returning users (who already have
  // consent in localStorage) don't trigger a replay on first effect run.
  private hadAnalyticsConsent = this.consent.analyticsAllowed();
  // Guards against re-replaying if the user revokes and re-grants consent
  // mid-session (e.g. via cookie settings page).
  private landingReplaySent = false;
  // gtag fires page_view for the first hit on config; skipping the first
  // NavigationEnd here keeps Angular from sending a duplicate.
  private firstNavigationSeen = false;

  constructor() {
    // Auth is now initialized via APP_INITIALIZER before routing starts

    // Listen to route changes to update current page and fire GA4 page_view on SPA navigation
    this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe((event: NavigationEnd) => {
        const route = event.urlAfterRedirects.split('/')[1] || 'home';
        this.currentRoute.set(route);
        this.cdr.detectChanges();

        // Capture ?openConversation=<id> deep links (any route).
        const openConversationId = this.router.parseUrl(event.urlAfterRedirects).queryParams[
          'openConversation'
        ];
        // Firestore doc ids: no slashes/whitespace — reject anything else so
        // the deep-link effect can't build an invalid document reference.
        if (
          typeof openConversationId === 'string' &&
          /^[A-Za-z0-9_-]{1,128}$/.test(openConversationId.trim())
        ) {
          this.pendingConversationId.set(openConversationId.trim());
        }

        // Remove the loading shell that masks the prerendered homepage flash
        // when users land on dynamic routes (e.g. /motorhome/:id) served via the
        // catch-all rewrite. See src/index.html for the inline script that adds it.
        if (typeof document !== 'undefined') {
          document.documentElement.classList.remove('app-loading-shell');
        }

        // Capture the very first path so we can replay it as page_view if
        // analytics consent is granted after this point. trackPageView is gated
        // by consent and silently drops events when denied — without this replay,
        // landingPage in GA4 would stay (not set) for every fresh visitor.
        const initialTitle = typeof document !== 'undefined' ? document.title : 'Hire My Motorhome';
        if (this.landingPath === null) {
          this.landingPath = event.urlAfterRedirects;
          this.landingTitle = initialTitle;
          this.landingHref =
            typeof window !== 'undefined' ? window.location.href : event.urlAfterRedirects;
        }

        // gtag('config') already fires the first page_view for the initial
        // route — skip Angular's emit on the first NavigationEnd to prevent
        // duplicate hits for consented users.
        if (!this.firstNavigationSeen) {
          this.firstNavigationSeen = true;
          return;
        }

        // Defer to the next macrotask so SeoService (which runs in component
        // ngOnInit/effects) has updated document.title before we read it.
        // Without this, page_title in GA4 is the PREVIOUS route's title.
        // Capture the expected URL in the closure so a rapid subsequent
        // navigation can't cause this timer to fire with a stale url.
        const expectedUrl = event.urlAfterRedirects;
        setTimeout(() => {
          if (this.router.url.split('?')[0] !== expectedUrl.split('?')[0]) return;
          const title = typeof document !== 'undefined' ? document.title : 'Hire My Motorhome';
          this.analytics.trackPageView(expectedUrl, title);
        }, 0);
      });

    // Replay landing page_view on the FIRST denied → granted consent transition.
    // Replays only once per app load: if the user later revokes and re-grants
    // (via /cookies settings), we don't fire a second replay.
    effect(() => {
      const allowed = this.consent.analyticsAllowed();
      if (
        allowed &&
        !this.hadAnalyticsConsent &&
        !this.landingReplaySent &&
        this.landingPath !== null
      ) {
        this.analytics.trackPageView(
          this.landingPath,
          this.landingTitle ?? 'Hire My Motorhome',
          this.landingHref ?? undefined,
        );
        this.landingReplaySent = true;
      }
      this.hadAnalyticsConsent = allowed;
    });

    // Open the messages drawer at the deep-linked conversation once the user
    // is authenticated, then remove the param from the URL. The drawer itself
    // reacts to the selected conversation landing in state (see
    // MessagesDrawerComponent) — here we only load/select it and open the drawer.
    effect(() => {
      const conversationId = this.pendingConversationId();
      const user = this.currentUser();
      if (!conversationId || !user) return;

      this.pendingConversationId.set(null);
      this.store.dispatch(new LoadConversation(conversationId));
      this.store.dispatch(new SelectConversation(conversationId));
      this.store.dispatch(new OpenMessagesDrawer(true));
      this.router.navigate([], {
        queryParams: { openConversation: null },
        queryParamsHandling: 'merge',
        replaceUrl: true,
      });
    });
  }

  onNavigate(route: string): void {
    this.router.navigate([route]);
    // Smooth scroll to top on navigation, similar to Figma implementation
    this.browser.scrollTo({ top: 0, behavior: 'smooth' });
  }

  onContactUs(): void {
    this.router.navigate(['/contact']);
    this.browser.scrollTo({ top: 0, behavior: 'smooth' });
  }
}
